import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  HeadingLevel,
  AlignmentType,
  BorderStyle,
  WidthType,
  convertInchesToTwip,
  PageBreak,
  Header,
  Footer,
  PageNumber,
} from 'docx';
import { DocumentModel, DocumentBlock, TextBlock, FormulaBlock, TableBlock, ImageBlock } from '../ast/document.types';
import { sanitizeFilename } from './documentValidator';

/**
 * Exports a DocumentModel AST directly to an editable Microsoft Word (.docx) document
 */
export async function exportDocumentToDocx(docModel: DocumentModel): Promise<Blob> {
  const meta = docModel.metadata;
  const children: (Paragraph | Table)[] = [];

  // Cover Page or Title Header
  const isCoverOnly = meta.documentType === 'cover_page';
  const coverSection = docModel.sections.find((s) => s.type === 'cover' && s.enabled);

  if (coverSection && coverSection.blocks.length > 0) {
    for (const block of coverSection.blocks) {
      const blockElements = convertBlockToDocx(block);
      children.push(...blockElements);
    }
    if (!isCoverOnly && docModel.sections.some((s) => s.type !== 'cover' && s.enabled)) {
      children.push(new Paragraph({ children: [new PageBreak()] }));
    }
  } else if (isCoverOnly || coverSection) {
    // Institution Header
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 200, after: 80 },
        children: [
          new TextRun({
            text: (meta.institution?.name || 'East West University').toUpperCase(),
            bold: true,
            size: 32, // 16pt
            color: '1A365D',
            font: 'Arial',
          }),
        ],
      })
    );

    if (meta.institution?.department) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 },
          children: [
            new TextRun({
              text: meta.institution.department,
              italics: true,
              size: 22, // 11pt
              color: '4A5568',
              font: 'Arial',
            }),
          ],
        })
      );
    }

    // Divider Line
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
        border: {
          bottom: { color: '2B6CB0', size: 12, space: 1, style: BorderStyle.SINGLE },
        },
      })
    );

    // Course Information
    if (meta.courseCode || meta.courseTitle) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { before: 200, after: 100 },
          children: [
            new TextRun({
              text: `${meta.courseCode ? meta.courseCode + ': ' : ''}${meta.courseTitle || ''}`,
              bold: true,
              size: 24, // 12pt
              color: '2B6CB0',
              font: 'Arial',
            }),
          ],
        })
      );
    }

    // Document Title
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 300, after: 150 },
        children: [
          new TextRun({
            text: meta.title || 'Laboratory Report',
            bold: true,
            size: 40, // 20pt
            color: '1A202C',
            font: 'Arial',
          }),
        ],
      })
    );

    if (meta.subtitle) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 500 },
          children: [
            new TextRun({
              text: meta.subtitle,
              italics: true,
              size: 24, // 12pt
              color: '718096',
              font: 'Arial',
            }),
          ],
        })
      );
    }

    // Metadata Table for Submitted By / Submitted To
    children.push(
      new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        borders: {
          top: { style: BorderStyle.SINGLE, size: 6, color: 'E2E8F0' },
          bottom: { style: BorderStyle.SINGLE, size: 6, color: 'E2E8F0' },
          left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
          right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
          insideHorizontal: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
          insideVertical: { style: BorderStyle.SINGLE, size: 6, color: 'E2E8F0' },
        },
        rows: [
          new TableRow({
            children: [
              new TableCell({
                width: { size: 50, type: WidthType.PERCENTAGE },
                margins: { top: convertInchesToTwip(0.15), bottom: convertInchesToTwip(0.15), left: convertInchesToTwip(0.15), right: convertInchesToTwip(0.15) },
                children: [
                  new Paragraph({
                    children: [new TextRun({ text: 'SUBMITTED BY:', bold: true, size: 18, color: '2B6CB0' })],
                    spacing: { after: 80 },
                  }),
                  new Paragraph({
                    children: [new TextRun({ text: meta.author.studentName || 'Student Name', bold: true, size: 22 })],
                  }),
                  ...(meta.author.studentId ? [new Paragraph({ children: [new TextRun({ text: `ID: ${meta.author.studentId}`, size: 20, color: '4A5568' })] })] : []),
                  ...(meta.author.sectionOrGroup ? [new Paragraph({ children: [new TextRun({ text: `Section: ${meta.author.sectionOrGroup}`, size: 20, color: '4A5568' })] })] : []),
                ],
              }),
              new TableCell({
                width: { size: 50, type: WidthType.PERCENTAGE },
                margins: { top: convertInchesToTwip(0.15), bottom: convertInchesToTwip(0.15), left: convertInchesToTwip(0.15), right: convertInchesToTwip(0.15) },
                children: [
                  new Paragraph({
                    children: [new TextRun({ text: 'SUBMITTED TO:', bold: true, size: 18, color: '4A5568' })],
                    spacing: { after: 80 },
                  }),
                  new Paragraph({
                    children: [new TextRun({ text: meta.instructor.instructorName || 'Instructor Name', bold: true, size: 22 })],
                  }),
                  ...(meta.instructor.designation ? [new Paragraph({ children: [new TextRun({ text: meta.instructor.designation, size: 20, color: '4A5568' })] })] : []),
                  ...(meta.instructor.department ? [new Paragraph({ children: [new TextRun({ text: meta.instructor.department, size: 20, color: '4A5568' })] })] : []),
                ],
              }),
            ],
          }),
        ],
      })
    );

    // Date
    children.push(
      new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { before: 400, after: 200 },
        children: [
          new TextRun({
            text: meta.submissionDate ? `Date of Submission: ${meta.submissionDate}` : `Date: ${new Date().toLocaleDateString()}`,
            italics: true,
            size: 20,
            color: '718096',
          }),
        ],
      })
    );

    if (!isCoverOnly && docModel.sections.some((s) => s.type !== 'cover' && s.enabled)) {
      children.push(new Paragraph({ children: [new PageBreak()] }));
    }
  }

  // Render Structured Sections
  for (const section of docModel.sections) {
    if (!section.enabled || section.type === 'cover') continue;

    // Section Heading (Heading 1)
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_1,
        spacing: { before: 360, after: 140 },
        children: [
          new TextRun({
            text: section.title.toUpperCase(),
            bold: true,
            size: 26, // 13pt
            color: '1A365D',
            font: 'Arial',
          }),
        ],
        border: {
          bottom: { color: 'CBD5E0', size: 6, space: 2, style: BorderStyle.SINGLE },
        },
      })
    );

    // Section Blocks
    for (const block of section.blocks) {
      const blockElements = convertBlockToDocx(block);
      children.push(...blockElements);
    }
  }

  // Build the docx Document
  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left: convertInchesToTwip(1),
              right: convertInchesToTwip(1),
            },
          },
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                  new TextRun({
                    text: meta.title || 'Student Document',
                    size: 16,
                    color: 'A0AEC0',
                    italics: true,
                  }),
                ],
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({ text: 'Page ', size: 18, color: '718096' }),
                  new TextRun({
                    children: [PageNumber.CURRENT],
                    size: 18,
                    color: '718096',
                  }),
                  new TextRun({ text: ' of ', size: 18, color: '718096' }),
                  new TextRun({
                    children: [PageNumber.TOTAL_PAGES],
                    size: 18,
                    color: '718096',
                  }),
                ],
              }),
            ],
          }),
        },
        children,
      },
    ],
  });

  return await Packer.toBlob(doc);
}

/**
 * Converts a document block into DOCX Paragraphs/Tables
 */
function convertBlockToDocx(block: DocumentBlock): (Paragraph | Table)[] {
  const result: (Paragraph | Table)[] = [];

  switch (block.type) {
    case 'text': {
      const textBlock = block as TextBlock;
      const text = textBlock.data.text || '';
      const lines = text.split('\n');

      let alignment: (typeof AlignmentType)[keyof typeof AlignmentType] = AlignmentType.LEFT;
      if (textBlock.data.formatting?.alignment === 'center') alignment = AlignmentType.CENTER;
      if (textBlock.data.formatting?.alignment === 'right') alignment = AlignmentType.RIGHT;
      if (textBlock.data.formatting?.alignment === 'justify') alignment = AlignmentType.JUSTIFIED;

      for (const line of lines) {
        if (!line.trim()) {
          result.push(new Paragraph({ spacing: { after: 100 } }));
          continue;
        }

        result.push(
          new Paragraph({
            alignment,
            spacing: { after: 120, line: 276 },
            children: [
              new TextRun({
                text: line,
                bold: textBlock.data.formatting?.bold,
                italics: textBlock.data.formatting?.italic,
                underline: textBlock.data.formatting?.underline ? {} : undefined,
                size: textBlock.data.level === 'heading1' ? 28 : textBlock.data.level === 'heading2' ? 24 : 22,
                color: '2D3748',
                font: 'Arial',
              }),
            ],
          })
        );
      }
      break;
    }

    case 'formula': {
      const formulaBlock = block as FormulaBlock;
      const latex = formulaBlock.data.latex || '';

      result.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { before: 160, after: 160 },
          border: {
            left: { color: '3182CE', size: 18, style: BorderStyle.SINGLE, space: 8 },
            top: { color: 'E2E8F0', size: 6, style: BorderStyle.SINGLE, space: 4 },
            bottom: { color: 'E2E8F0', size: 6, style: BorderStyle.SINGLE, space: 4 },
            right: { color: 'E2E8F0', size: 6, style: BorderStyle.SINGLE, space: 4 },
          },
          children: [
            new TextRun({
              text: `[ Formula: ${latex} ]`,
              italics: true,
              bold: true,
              size: 22,
              color: '2B6CB0',
              font: 'Cambria Math',
            }),
            ...(formulaBlock.data.equationNumber
              ? [
                  new TextRun({
                    text: `   ${formulaBlock.data.equationNumber}`,
                    size: 20,
                    color: '718096',
                  }),
                ]
              : []),
          ],
        })
      );

      if (formulaBlock.data.caption) {
        result.push(
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 140 },
            children: [
              new TextRun({
                text: `Equation: ${formulaBlock.data.caption}`,
                italics: true,
                size: 18,
                color: '718096',
              }),
            ],
          })
        );
      }
      break;
    }

    case 'table': {
      const tableBlock = block as TableBlock;
      const cells = tableBlock.data.cells || [];
      if (cells.length === 0) break;

      const numCols = cells[0].length || 1;
      const colWidthPercent = Math.floor(100 / numCols);

      const tableRows = cells.map((row, rIdx) => {
        const isHeader = rIdx === 0 && tableBlock.data.hasHeaderRow !== false;
        return new TableRow({
          children: row.map((cell) => {
            return new TableCell({
              width: { size: colWidthPercent, type: WidthType.PERCENTAGE },
              shading: isHeader ? { fill: 'EDF2F7' } : undefined,
              margins: {
                top: convertInchesToTwip(0.08),
                bottom: convertInchesToTwip(0.08),
                left: convertInchesToTwip(0.1),
                right: convertInchesToTwip(0.1),
              },
              children: [
                new Paragraph({
                  alignment: cell?.alignment === 'center' ? AlignmentType.CENTER : cell?.alignment === 'right' ? AlignmentType.RIGHT : AlignmentType.LEFT,
                  children: [
                    new TextRun({
                      text: cell?.content || '',
                      bold: isHeader || cell?.bold,
                      size: isHeader ? 20 : 18,
                      color: isHeader ? '1A202C' : '2D3748',
                      font: 'Arial',
                    }),
                  ],
                }),
              ],
            });
          }),
        });
      });

      result.push(
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          borders: {
            top: { style: BorderStyle.SINGLE, size: 6, color: 'CBD5E0' },
            bottom: { style: BorderStyle.SINGLE, size: 6, color: 'CBD5E0' },
            left: { style: BorderStyle.SINGLE, size: 6, color: 'CBD5E0' },
            right: { style: BorderStyle.SINGLE, size: 6, color: 'CBD5E0' },
            insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: 'E2E8F0' },
            insideVertical: { style: BorderStyle.SINGLE, size: 4, color: 'E2E8F0' },
          },
          rows: tableRows,
        })
      );

      if (tableBlock.data.caption) {
        result.push(
          new Paragraph({
            spacing: { before: 60, after: 140 },
            children: [
              new TextRun({
                text: `Table: ${tableBlock.data.caption}`,
                italics: true,
                size: 18,
                color: '718096',
              }),
            ],
          })
        );
      }
      break;
    }

    case 'image': {
      const imgBlock = block as ImageBlock;
      if (imgBlock.data.caption) {
        result.push(
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 140, after: 140 },
            children: [
              new TextRun({
                text: `[ Figure: ${imgBlock.data.caption} ]`,
                bold: true,
                italics: true,
                size: 20,
                color: '4A5568',
              }),
            ],
          })
        );
      }
      break;
    }

    case 'shape': {
      const shapeBlock = block as any;
      const shapeType = shapeBlock.data?.shapeType || 'rectangle';
      const isDivider = shapeType === 'divider' || shapeType === 'line';
      result.push(
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { before: 120, after: 120 },
          border: isDivider ? {
            bottom: { color: '2B6CB0', size: 12, space: 1, style: BorderStyle.SINGLE },
          } : {
            top: { color: 'CBD5E0', size: 6, space: 4, style: BorderStyle.SINGLE },
            bottom: { color: 'CBD5E0', size: 6, space: 4, style: BorderStyle.SINGLE },
            left: { color: 'CBD5E0', size: 6, space: 4, style: BorderStyle.SINGLE },
            right: { color: 'CBD5E0', size: 6, space: 4, style: BorderStyle.SINGLE },
          },
          children: [
            new TextRun({
              text: isDivider ? '' : `[ Shape: ${shapeType} ]`,
              size: 18,
              italics: true,
              color: '718096',
            }),
          ],
        })
      );
      break;
    }

    case 'page_break': {
      result.push(new Paragraph({ children: [new PageBreak()] }));
      break;
    }
  }

  return result;
}

/**
 * Downloads a DOCX Blob as a file in the browser
 */
export function downloadDocxBlob(blob: Blob, filename: string = 'document.docx') {
  const safeName = sanitizeFilename(filename, 'docx');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = safeName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
